import * as React from "react";

import { AttachmentList } from "../Common/Attachments/AttachmentList";
import { Button } from "../Common/Button/Button";
import { LoadingIcon } from "../Common/LoadingIcon/LoadingIcon";
import { PublishedCalendarList } from "../Common/PublishedCalendars/PublishedCalendarList";

import { IEvent } from "../../classes/event";
import { IText } from "../../classes/text";

import * as dates from "../../reducers/changeTimePeriod";

import * as buttonEvents from "../Common/buttonEvents";
import { CustomAttributeList } from "./CustomAttributes/CustomAttributeList";
import { DeleteEventButton } from "./DeleteEventButton";
import { ParticipantsDisplaySection } from "./ParticipantsDisplaySection";
import { RegistrationDisplaySection } from "./RegistrationDisplaySection";

export interface IEventViewProps {
  calendarId: number;
  contextId: number;
  currentEvent: IEvent;
  iCalStartDate: Date;
  eventId: number;
  isLoading: boolean;
  localStartDate: Date;
  localEndDate: Date;
  pageId: number;
  portletInstanceId: number;
  selectedDate: Date;
  text: IText;
  useModernAddAndEditEventScreen: boolean;
  userDateFormat: string;
  viewMode: string;
  requestEventDetailFromService: (portletInstanceId: number, calendarId: number, eventId: number, localStartDate: Date, localEndDate) => any;
  onSetEventIdToZero: () => any;
  onSetEventScreenMode: (eventScreenMode: string) => any;
  onStartAjaxCall: (event: any) => any;
  registerForEventFromService: (eventId: number, calendarId: number) => any;
  unregisterFromEventFromService: (eventId: number, calendarId: number, localStartDate: Date, localEndDate: Date) => any;
  deleteEventFromService: (portletInstanceId: number, objectId: number, parentId: number, contextId: number, deleteThisEventInstanceOnly: boolean, eventInstanceDate: Date) => any;
  exportFileFromService: (calendarId: number, eventId: number, isAllDay: boolean, title: string, location: string, description: string, category: string, localStartDate: Date, localEndDate: Date) => any;
}

export class EventDetailsView extends React.Component<IEventViewProps, any> {

  public constructor(props: any, context: any) {
    super(props, context);

    this.requestEventDetailFromService();

    buttonEvents.importProps(props);
  }

  public requestEventDetailFromService() {
      this.props.requestEventDetailFromService(this.props.portletInstanceId, this.props.calendarId, this.props.eventId, this.props.localStartDate, this.props.localEndDate);
  }

  public exportFileFromService(localStartDate: Date, localEndDate: Date) {
    this.props.exportFileFromService(this.props.calendarId, this.props.eventId, this.props.currentEvent.isAllDay, this.props.currentEvent.name, this.props.currentEvent.location, this.props.currentEvent.description, this.props.currentEvent.category, localStartDate, localEndDate);
  }

  public render() {
    // Loading and classnames:
    const eventId = this.props.eventId;
    const isLoading = this.props.isLoading;
    const loadingEventsText = this.props.text.eventViews.loadingEvents;

    let calViewClass = "CAL-view";
    if (isLoading) { calViewClass += " disabled"; }

    // Event details data:
    const event = this.props.currentEvent;
    let title = this.props.text.eventViews.loading;
    let time = null;
    let icon = null;
    let location = null;
    let region = null;
    let category = null;
    let description = null;
    let startEndDate = null;
    let contact = null;
    let originatingCalendarURL = null;
    let completeOriginatingCalendarURL = null;
    let participantMembers = null;
    let participantNonMembers = null;
    let hasRecurrance = null;
    let attachments = null;
    let publishedCalendars = null;
    let recurrenceDescription = null;
    let priceValue = null;
    let priceCurrency = null;
    let customAttributes = null;
    let registration = null;
    let calendarId = null;
    let eventMergeType: string = null;
    let isICalendar: boolean = false;
    let isTwoWay: boolean = false;

    if (!isLoading && event) {
      title = event.name;
      time = dates.eventDetailDescription(event, this.props.text.eventViews.allDay);
      icon = event.icon;
      location = event.location;
      region = event.region;
      category = event.category;
      description = event.description;
      startEndDate = event.preformatted_localStartDate + " - " + event.preformatted_localEndDate;
      contact = event.contact;
      participantMembers = event.participantMembers;
      participantNonMembers = event.participantNonMembers;
      hasRecurrance = event.hasRecurrence;
      recurrenceDescription = event.recurrenceDescription;
      attachments = event.attachments;
      priceValue = event.priceValue;
      priceCurrency = event.priceCurrency;
      publishedCalendars = event.publishedCalendars;
      customAttributes = event.customAttributes;
      registration = event.registration;
      calendarId = this.props.calendarId;
      eventMergeType = event.eventMergeType;
      isICalendar = event.isICalendar;
      isTwoWay = event.isTwoWay;

      originatingCalendarURL = event.originatingCalendarURL;
      if (originatingCalendarURL.trim() !== "") {
        completeOriginatingCalendarURL = originatingCalendarURL + "&view=" + this.props.viewMode;
      }

    }

    // Back to Event View button event handler.
    const onBack = (e) => {
      e.preventDefault();
      this.props.onSetEventScreenMode("LIST");
      this.props.onSetEventIdToZero();
    };

    // JS-File-Downloader takes the data and creates/downloads file based on the data passed to it
    const onExportFile = (e) => {
      e.preventDefault();
      const localStartDate = event.localStartDate;
      const localEndDate = event.localEndDate;
      this.exportFileFromService(localStartDate, localEndDate);
    };

    const onUnregister = () => {
      const localStartDate = event.localStartDate;
      const localEndDate = event.localEndDate;
      this.props.unregisterFromEventFromService(eventId, calendarId, localStartDate, localEndDate);
    };

    const onRegister = () => {
      this.props.registerForEventFromService(eventId, calendarId);
    };

    // Create link to take to admin view of this event.
    const contextId = this.props.contextId;
    const selectedDate = this.props.selectedDate;
    const pageId = this.props.pageId;
    const parentId = this.props.calendarId;
    const portletInstanceId = this.props.portletInstanceId;
    const viewMode = this.props.viewMode;
    const onStartAjaxCall = this.props.onStartAjaxCall;

    const onEventInAdminMode = (e) => {
      if (this.props.useModernAddAndEditEventScreen) {
        e.preventDefault();
        this.props.onSetEventScreenMode("EDIT");
      } else {
        buttonEvents.onEventInAdminMode(e, eventId, event.reference, contextId, selectedDate, pageId, parentId, portletInstanceId, viewMode, onStartAjaxCall);
      }
    };

    // Button settings.
    const backbuttonID = "backbutton_" + portletInstanceId;
    const adminbuttonID = "adminbutton_" + portletInstanceId;
    const exportbuttonID = "exportbutton_" + portletInstanceId;

    // Whether to show edit and delete buttons
    let showEditEventButton = false;
    let showDeleteEventButton = false;
    if (!isLoading && event && (eventMergeType !== "MergedCalendar" && !isICalendar || isTwoWay) && originatingCalendarURL === "") {
      showEditEventButton = event.hasEditEventPermission;
      showDeleteEventButton = event.hasDeleteEventPermission;
    }

    return (
      <>
        <div className="CAL-EVENT-toolbar">
          <div className="CAL-EVENT-back">
            <Button
              itemID={backbuttonID}
              cssID="CMS_bMarginR"
              text={this.props.text.eventViews.backToCalendar}
              visibleText={true}
              fontawesomeIconCSS="fa-arrow-left"
              onClick={onBack}
              disabled={isLoading}
              removeJsNavSystem={true}
            />
          </div>
          <div className="CAL-EVENT-controls">
            {showEditEventButton &&
              <Button
                itemID={adminbuttonID}
                cssID="CMS_bWarn CMS_bMarginR"
                text={this.props.text.eventViews.editEvent}
                visibleText={false}
                fontawesomeIconCSS="fa-pencil"
                onClick={onEventInAdminMode}
                disabled={isLoading}
                removeJsNavSystem={true}
                isCMSButton={true}
              />
            }
            <Button
              itemID={exportbuttonID}
              cssID="CMS_bAction CMS_bMarginR"
              text={this.props.text.eventViews.exportEvent}
              visibleText={false}
              fontawesomeIconCSS="fa-upload"
              onClick={onExportFile}
              disabled={isLoading}
              removeJsNavSystem={true}
              isCMSButton={true}
            />
            {showDeleteEventButton &&
              <DeleteEventButton
                calendarId={calendarId}
                contextId={contextId}
                currentEvent={event}
                eventId={eventId}
                isLoading={isLoading}
                portletInstanceId={portletInstanceId}
                text={this.props.text}
                onSetEventIdToZero={this.props.onSetEventIdToZero}
                onSetEventScreenMode={this.props.onSetEventScreenMode}
                deleteEventFromService={this.props.deleteEventFromService}
              />
            }

            {/*<a className="CAL-button CAL-square" href="#"><span className="offScreen">Share Event</span><em className="fa fa-share-alt" aria-hidden="true"></em></a>
            <a className="CAL-button CAL-square" href="#"><span className="offScreen">Delete Event</span><em className="fa fa-trash" aria-hidden="true"></em></a>*/}
          </div>
        </div>
        <div className="CAL-box">
          <div className="CAL-EVENT-title">
            {icon &&
              <img src={icon} aria-hidden="true" />
            }
            <span>{title}</span>
            {hasRecurrance &&
              <em className="fa fa-refresh" aria-hidden="true" style={{ paddingLeft: "5px" }}></em>
            }
          </div>
          <div className="CAL-EVENT-content">
            <LoadingIcon displayIcon={isLoading} text={loadingEventsText} />
            {!isLoading && event &&
              <ul>
                <li className="CAL-EVENT-cRow">
                  <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.dateAndTime}</div>
                  <div className="CAL-EVENT-cR-content">{time}</div>
                </li>
                {originatingCalendarURL && event && event.hasEditEventPermission &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.originatingSource}</div>
                    <div className="CAL-EVENT-cR-content"><a href={completeOriginatingCalendarURL} target="_blank">{completeOriginatingCalendarURL}</a></div>
                  </li>
                }
                {location &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.location}</div>
                    <div className="CAL-EVENT-cR-content">{location}</div>
                  </li>
                }
                {registration &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.registration}</div>
                    <RegistrationDisplaySection
                      registration={registration}
                      registerForEventFromService={onRegister}
                      unregisterFromEvent={onUnregister}
                      eventViewText={this.props.text.eventViews} />
                  </li>
                }
                {category &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.category}</div>
                    <div className="CAL-EVENT-cR-content">{category}</div>
                  </li>
                }
                {hasRecurrance &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.recurrence}</div>
                    <div className="CAL-EVENT-cR-content">{recurrenceDescription}</div>
                  </li>
                }
                {description &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.description}</div>
                    <div className="CAL-EVENT-cR-content" dangerouslySetInnerHTML={{ __html: `${description}` }}>
                      {/* Have to use dangerouslySetHTML to force React to render raw HTML. */}
                    </div>
                  </li>
                }
                {region &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.region}</div>
                    <div className="CAL-EVENT-cR-content">{region}</div>
                  </li>
                }
                {contact &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.contact}</div>
                    <div className="CAL-EVENT-cR-content">{contact}</div>
                  </li>
                }
                {(participantMembers || participantNonMembers) &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.participants}</div>
                    <ParticipantsDisplaySection
                      members={participantMembers}
                      nonMembers={participantNonMembers}
                      eventViewText={this.props.text.eventViews} />
                  </li>
                }
                {(priceValue || priceValue > 0) &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.price}</div>
                    <div className="CAL-EVENT-cR-content">{priceValue} {priceCurrency}</div>
                  </li>
                }
                {(customAttributes && customAttributes.length > 0) &&
                  <li className="CAL-EVENT-cRow">
                    <CustomAttributeList customAttributes={customAttributes} />
                  </li>
                }
                {(attachments && attachments.length > 0) &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.attachments}</div>
                    <AttachmentList
                      attachments={attachments}
                      eventId={eventId}
                      eventViewText={this.props.text.eventViews} />
                  </li>
                }
                {(publishedCalendars && publishedCalendars.length > 0) &&
                  <li className="CAL-EVENT-cRow">
                    <div className="CAL-EVENT-cR-title">{this.props.text.eventViews.publishedCalendars}</div>
                    <PublishedCalendarList calendars={publishedCalendars} eventViewText={this.props.text.eventViews} />
                  </li>
                }
              </ul>
            }
          </div>
        </div>

      </>
    );
  }

};
